<template>
    <div v-show="!hideQuickfinder" class="quickfinder">
        <LoadingSpinner v-if="loading" />

        <div class="row gx-0">
            <div class="col-xl-4 d-xl-block d-none">
                <div class="ratio ratio-3x4" v-html="img"></div>
            </div>
            <div class="col-xl-8">
                <form :action="submitUrl">
                    <div class="quickfinder__body">
                        <div class="quickfinder__row-item d-flex align-items-center">
                            <span class="d-inline-block me-4">{{ translate('quickfinder.i.want') }}</span>
                            <CustomSelect
                                :label="translate('quickfinder.category')"
                                labelClassName="visually-hidden"
                                class-name="form-group--bold"
                                id="category"
                                name="category"
                                :key="ticketCategoriesOptions"
                                @change-value="changeTicketCategory($event)"
                                :options="ticketCategoriesOptions" />
                        </div>

                        <div class="quickfinder__row-item d-flex align-items-center">
                            <span class="d-inline-block me-4">{{ translate('quickfinder.for') }}</span>
                            <CustomSelect
                                :label="translate('quickfinder.persons')"
                                labelClassName="visually-hidden"
                                class-name="form-group--bold"
                                id="persons"
                                name="persons"
                                :selected-option="selectedOption"
                                :key="personsOptions"
                                :options="personsOptions" @update:model-value="updatePrice($event)" />
                        </div>

                        <div class="quickfinder__row-item quickfinder__row-item--nowrap d-flex gap-4 align-items-center flex-wrap" v-if="showDate">
                            <span>{{ translate('quickfinder.for') }}</span>

                            <div class="d-flex gap-3">
                                <Radio
                                    :label="translate('quickfinder.today')"
                                    labelClassName="mb-0"
                                    class-name="form-group--bold"
                                    id="today"
                                    name="date"
                                    :checked="radioToday"
                                    :value="dateToISOString(today)"
                                    @update:checked="radioToday = $event; clearDatepicker();"
                                    v-if="showToday"/>

                                <Radio
                                    :label="translate('quickfinder.tomorrow')"
                                    labelClassName="mb-0"
                                    class-name="form-group--bold"
                                    id="tomorrow"
                                    name="date"
                                    :value="dateToISOString(tomorrow)"
                                    :checked="radioTomorrow"
                                    @update:checked="radioTomorrow = $event; clearDatepicker();"
                                    v-if="showTomorrow"/>
                            </div>

                            <div class="d-flex flex-nowrap gap-4 align-items-center">
                                <span v-if="showToday && showTomorrow"> {{ translate('quickfinder.or.on') }}</span>

                                <Datepicker
                                    :label="translate('quickfinder.date.label')"
                                    :placeholder="translate('quickfinder.date.label')"
                                    class-name="form-group--bold quickfinder__datepicker"
                                    id="date"
                                    name="date"
                                    :max-date="maxDate"
                                    :min-date="minDate"
                                    :disable="disabledDates"
                                    :key="[disabledDates, minDate, maxDate, date].toString()"
                                    :model-value="date"
                                    :disabled="datepickerDisabled"
                                    :label-class-name="'visually-hidden'"
                                    v-model="date"/>
                            </div>
                        </div>
                        <div class="quickfinder__row-item d-flex gap-4 align-items-center" v-else>
                            <span>{{ translate('quickfinder.for') }}</span>
                            <strong>{{ datePlaceholderString }}</strong>
                        </div>

                        <div class="quickfinder__row-item">
                            <div class="d-flex justify-content-between">
                                <div>
                                    <template v-if="price > 0">
                                      <div>{{ translate('quickfinder.dailyprice') }}</div>
                                      <div class="quickfinder__price">
                                            <Price :value="price" />
                                      </div>
                                    </template>
                                    <div v-else>{{ translate('quickfinder.NoPriceFound') }}</div>
                                </div>

                                <button
                                  type="submit"
                                  class="btn btn-primary btn-icon-text"
                                  @click="radioToday || radioTomorrow ? datepickerDisabled = true : ''; trackSubmit();">
                                    <span class="icon icon-bag btn-icon-text__icon" aria-hidden="true"></span>
                                  {{ translate('quickfinder.buy') }}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>

  <div v-show="hideQuickfinder">
    <a :href="overviewUrl" class="btn btn-primary">{{translate('quickfinder.to-ticket-overview')}}</a>
  </div>

  <Toast :store="{errors: errors}" />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, defineProps, nextTick, watch } from 'vue';
import Datepicker from "./components/form-elements/Datepicker.vue";
import Select from "./components/form-elements/Select.vue";
import {fetcher} from "../utils/utils.ts";
import {trackEvent} from "../utils/tracking.ts";
import Radio from "./components/form-elements/Radio.vue";
// @ts-ignore
import * as lazyImg from "@elements/lazy-img";
// @ts-ignore
import { ISOStringToDate, dateToISOString } from "@elements/date-utils";
import LoadingSpinner from "./components/LoadingSpinner.vue";
import Price from "./components/Price.vue";
// @ts-ignore
import { translate } from "@elements/translations";
import Toast from "./components/Toast.vue";
import CustomSelect from "./components/form-elements/CustomSelect.vue";
// @ts-ignore
import {assert, object, number, string, array, optional, boolean} from 'superstruct'

const props = defineProps<{
    img: string,
    url: string,
    formAction: string,
    overviewUrl: string
}>()

const ticketCategoriesOptions = ref<String[]>([]);
const personsOptions = ref<String[]>([]);
const showDate = ref(true);
const disabledDates = ref(["2023-09-21"]);
const minDate = ref("");
const maxDate = ref("");
const loading = ref(false);
const datePlaceholderString = ref('nächste Saison');
const date = ref<Date>();
const radioToday = ref(true);
const radioTomorrow = ref(false);
const price = ref(0);
const selectedOption = ref('');
const errors = ref([]);
const submitUrl = ref(props.formAction);
//only for removing this parameter from the url if datepicker is empty
const datepickerDisabled = ref(false);
const hideQuickfinder = ref(false);
const ticketCategory = ref<String>('undefined');

const today = new Date();
const tomorrow = new Date(today);
tomorrow.setDate(tomorrow.getDate() + 1);

// Fetch data from JSON file
onMounted(async () => {
    try {
        await fetchInitialData();

    } catch (error) {
        console.error('Error fetching data: ', error);
        errors.value.push(error)
    }
    await nextTick(lazyImg.init());
});

let QuickfinderResult = object({
  categories: array(object({
    id: string(),
    value: string(),
    label: string(),
    submitUrl: string()
  })),
  persons: array(object({
    value: string(),
    label: string(),
    price: number(),
  })),
  showDate: boolean(),
  datePlaceholderString: optional(string()),
  disabledDates: optional(array(string())),
  minDate: optional(string()),
  maxDate: optional(string()),
});

const fetchInitialData = async () => {
    loading.value = true;

    await fetcher(props.url).then((result) => {

      try {
        assert(result, QuickfinderResult);

        ticketCategoriesOptions.value = result.categories.map((type) => {
          return {
            id: type.id,
            value: type.value,
            label: type.label,
            submitUrl: type.submitUrl
          }
        });

        personsOptions.value = result.persons.map((type) => {
          return {
            value: type.value,
            label: type.label,
            price: type.price
          }
        });

        price.value = personsOptions.value[0].price;
        selectedOption.value = personsOptions.value[0].value;

        showDate.value = result.showDate;
        datePlaceholderString.value = result.datePlaceholderString;
        disabledDates.value = result.disabledDates;
        minDate.value = result.minDate;
        maxDate.value = result.maxDate;
        loading.value = false;
      } catch (error: unknown) {
        console.log(error);
        errors.value.push(error.message);
      }

    }).catch((error) => {
        console.log(error);
        errors.value.push(error)
    });

}

const changeTicketCategory = async (value) => {
    loading.value = true;
    ticketCategory.value = value;

    //change submitUrl to the one from the selected category
    submitUrl.value = ticketCategoriesOptions.value.find((category) => category.value === value).submitUrl;

    const separator = props.url.includes('?') ? '&' : '?';
    await fetcher(props.url + separator + 'category=' + value).then((result) => {

      try {
        assert(result, QuickfinderResult);

        personsOptions.value = result.persons.map((type) => {
          return {
            value: type.value,
            label: type.label,
            price: type.price
          }
        });

        price.value = personsOptions.value[0].price;
        selectedOption.value = personsOptions.value[0].value;

        showDate.value = result.showDate;
        datePlaceholderString.value = result.datePlaceholderString;
        disabledDates.value = result.disabledDates;
        minDate.value = result.minDate;
        maxDate.value = result.maxDate;

        loading.value = false;
      } catch (error: unknown) {
        console.log(error);
        errors.value.push(error.message);
      }

    }).catch((error) => {
        console.log(error);
        errors.value.push(error)
    });
}

const clearDatepicker = () => {
    date.value = null;
}

const showToday = computed(() => {
    let minimumDate = new Date(ISOStringToDate(minDate.value));
    let maximumDate = new Date(ISOStringToDate(maxDate.value));
    let isDisabled = false;

    disabledDates.value.map((date) => {
        dateToISOString(today).slice(0,10) === date ? isDisabled = true : null;
    })

    return (minimumDate <= today && maximumDate >= today) && !isDisabled;
});
const showTomorrow = computed(() => {
    let minimumDate = new Date(ISOStringToDate(minDate.value));
    let maximumDate = new Date(ISOStringToDate(maxDate.value));
    let isDisabled = false;

    disabledDates.value.map((date) => {
        dateToISOString(tomorrow).slice(0,10) === date ? isDisabled = true : null;
    })

    // radioTomorrow.value = (minimumDate <= tomorrow && maximumDate >= tomorrow) && !isDisabled;
    return (minimumDate <= tomorrow && maximumDate >= tomorrow) && !isDisabled;
});

watch(date , (newValue, oldValue) => {
    if(newValue !== null) {
        radioToday.value = false;
        radioTomorrow.value = false;
    }
});

const updatePrice = (value) => {
    selectedOption.value = value;
    personsOptions.value.map((person) => {
      if(person.value === value) {
        price.value = person.price;
        }
    });

}

watch(errors, (newValue, oldValue) => {
  if(newValue.length > 0) {
        hideQuickfinder.value = true;
    }
}, {deep: true});

const trackSubmit = () => {
  let trackingDate = today;

  if(radioTomorrow.value){
    trackingDate = tomorrow;
  }else if(date.value !== null && date.value !== undefined) {
    trackingDate = ISOStringToDate(date.value);
  }

  trackEvent('click_body_quickfinder',
     {
       category: ticketCategory.value,
       amount_people: selectedOption.value,
       date: showDate ? dateToISOString(trackingDate) : datePlaceholderString.value
     })
}

</script>

<style lang="scss" scoped></style>